<template>
  <v-container>
    <v-row justify-center>
      <v-col>
        <v-divider />

        <!-- Titre personnalisé -->
        <v-row align="center" justify="center">
          <v-col cols="12" lg="3" xl="2">
            <h4 class="part-title">Titre personnalisé :</h4>
          </v-col>
          <v-col cols="12" lg="5" xl="3">
            <v-text-field
              v-model="currentDiscipline.workFormCustomTitle"
              @change="updateDisciplineCustomLabel(currentDiscipline.workFormCustomTitle)"
              placeholder="Formes de travail"
              outlined
              dense
              class="mr-5"
              :rules="[rules.counter]"
              counter
              maxlength="30"
              clearable
              :disabled="!canManageCurrentDiscipline"
            />
          </v-col>
          <v-col cols="12" lg="4" xl="7">
            <span style="font-style: italic">Le titre personnalisé sera affiché à la place de "Formes de travail".</span>
          </v-col>
        </v-row>

        <v-divider v-if="canManageCurrentDiscipline" />

        <!-- Add item -->
        <v-row v-if="canManageCurrentDiscipline">
          <v-col cols="12" lg="3" xl="2">
            <h4 class="part-title">Ajouter un élément :</h4>
          </v-col>
          <v-col cols="12" lg="5" xl="3">
            <v-text-field
              v-model="newWorkForm.title"
              outlined
              dense
              class="mr-5"
              :rules="[rules.required, rules.counter]"
              counter
              maxlength="30"
              hide-details="auto"
              clearable
            />
          </v-col>
          <v-col cols="12" lg="4" xl="7">
            <v-btn v-on:click="addWorkForm()" color="primary" dark class="mb-2" elevation="2" :disabled="!newWorkForm.title"> Ajouter </v-btn>
          </v-col>
        </v-row>

        <v-divider />

        <!-- List of items -->
        <v-row v-if="workForms && workForms.length > 0">
          <v-col cols="12">
            <h4 class="part-title">{{ getCutomTitle() }}</h4>
          </v-col>
          <v-col>
            <v-row v-for="workForm in workForms" :key="workForm.id" class="ma-0 pa-1">
              <v-col cols="6" lg="6" xl="3" class="no-vert-padding">
                <v-text-field
                  @change="updateWorkForm(workForm)"
                  v-model="workForm.title"
                  outlined
                  dense
                  class="mr-5"
                  :rules="[rules.required, rules.counter]"
                  counter
                  maxlength="30"
                  hide-details="auto"
                  :disabled="!canManageCurrentDiscipline"
                />
              </v-col>
              <v-col cols="6" lg="6" xl="9" class="no-vert-padding" v-if="canManageCurrentDiscipline">
                <v-btn v-on:click="deleteWorkForm(workForm.id)" color="red" dark class="mb-2" elevation="2"> Supprimer </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import store from "@/store";
import PermissionsService from "@/services/PermissionsService";
import { getWorkFormForDisciplineAsync, createWorkFormForDisciplineAsync, updateWorkFormAsync, deleteWorkFormAsync } from "@/api/workFormApi";
import { getDisciplineAsync, updateDisciplineAsync, getIsManagerPartOfDisciplineAsync } from "@/api/disciplinesApi";
import AuthService from "@/services/AuthService";
import { getIsUserPartOfFederationAsync } from "@/api/federationsApi";

export default {
  props: {
    disciplineId_prop: [Number, String],
  },

  data: () => ({
    isCurrentUserPartOfFederation: false,
    isCurrentUserPartOfDiscipline: false,
    authService: AuthService,
    federationId: null,
    disciplineId: null,
    workForms: [],
    currentDiscipline: {},
    customTitle: "",
    newWorkForm: { title: "Nouvel élément" },
    rules: {
      required: (value) => !!value || "Requis",
      counter: (value) => (value ? value.length <= 30 : true),
    },
  }),

  watch: {
    $route() {
      this.initPage();
    },
  },

  computed: {
    canViewCurrentDiscipline() {
      // Can view own discipline and is part of current discipline
      let canViewOwnDisciplineAndIsPartOfCurrentDiscipline = PermissionsService.DisciplinesPermissionsService.canViewOwnDiscipline && this.isCurrentUserPartOfDiscipline;
      // Can view all Disciplines
      let canViewAllDisciplines = PermissionsService.DisciplinesPermissionsService.canViewAllDisciplinesOwnFederation;
      return canViewOwnDisciplineAndIsPartOfCurrentDiscipline || canViewAllDisciplines;
    },
    canManageCurrentDiscipline() {
      // Can manage own discipline and is part of current discipline
      let canManageCurrentDisciplineAndIsPartOfCurrentDiscipline =
        PermissionsService.DisciplinesPermissionsService.canManageOwnDiscipline && this.isCurrentUserPartOfDiscipline;
      // Can manage all disciplines of the current federation
      let canManageAllDisciplines = PermissionsService.DisciplinesPermissionsService.canManageAllDisciplinesOwnFederation && this.isCurrentUserPartOfFederation;
      return canManageCurrentDisciplineAndIsPartOfCurrentDiscipline || canManageAllDisciplines;
    },
  },

  async mounted() {
    await this.initPage();
  },

  methods: {
    async initPage() {
      // Override the props values from the url if provided
      this.federationId = this.$route.params.federationId ? parseInt(this.$route.params.federationId) : null;
      this.disciplineId = this.$route.params.disciplineId ? parseInt(this.$route.params.disciplineId) : this.disciplineId_prop;

      // Check if the current user is part of current federation
      this.isCurrentUserPartOfFederation = await getIsUserPartOfFederationAsync(this.authService.userId, this.federationId);      
      // Check if the current user is part of current discipline
      this.isCurrentUserPartOfDiscipline = await getIsManagerPartOfDisciplineAsync(this.authService.userId, this.disciplineId);

      // Check if the user can see this page
      if (!this.canViewCurrentDiscipline) {
        store.dispatch("setSnackbar", {
          title: `Non Autorisé`,
          text: `Vous n'êtes pas autorisé à consulter cette ressource.`,
          status: "warning",
          timeout: 2000,
        });

        this.$router.replace("/");
      }

      this.getDisciplineForCustomLabel();
      this.getWorkFormsForDiscipline();
    },

    // get the custom title if exists
    getCutomTitle() {
      if (this.currentDiscipline && this.currentDiscipline.workFormCustomTitle) {
        return this.currentDiscipline.workFormCustomTitle;
      } else {
        return "Formes de travail";
      }
    },

    // Add a new Work Form to the discipline
    async addWorkForm() {
      this.newWorkForm.disciplineId = this.disciplineId;

      var result = await createWorkFormForDisciplineAsync(this.disciplineId, this.newWorkForm);

      if (result) {
        store.dispatch("setSnackbar", {
          title: "Ajouté",
          text: "L'élément de la liste a bien été ajouté",
          status: "information",
          timeout: 1000,
        });
      }

      this.getWorkFormsForDiscipline();
    },

    // Delete an Work Form
    async deleteWorkForm(id) {
      if (!id || !confirm("Êtes-vous sûr de vouloir supprimer cet élément ?")) return false;

      var result = await deleteWorkFormAsync(id);

      if (result) {
        store.dispatch("setSnackbar", {
          title: "Supprimé",
          text: "L'élément de la liste a bien été supprimé",
          status: "information",
          timeout: 1000,
        });
      }

      this.getWorkFormsForDiscipline();
    },

    // Update an Work Form
    async updateWorkForm(workForm) {
      if (workForm.title) {
        var result = await updateWorkFormAsync(workForm);

        if (result) {
          store.dispatch("setSnackbar", {
            title: "Modifié",
            text: "L'élément de la liste a bien été modifié",
            status: "information",
            timeout: 1000,
          });
        }

        this.getWorkFormsForDiscipline();
      }
    },

    // Get the list of Work Form
    async getWorkFormsForDiscipline() {
      this.workForms = await getWorkFormForDisciplineAsync(this.disciplineId);
    },

    // Get the current discipline to use the custom Work Form title
    async getDisciplineForCustomLabel() {
      this.currentDiscipline = await getDisciplineAsync(this.disciplineId);

      if (!this.currentDiscipline) {
        store.dispatch("setSnackbar", {
          title: "Erreur",
          text: "Impossible de récupérer la discipline auprès du serveur",
          status: "error",
          timeout: 5000,
        });
      } else {
        this.customTitle = this.currentDiscipline.workFormCustomTitle ? this.currentDiscipline.workFormCustomTitle : this.customTitle;
      }
    },

    // Get the current discipline to use the custom Work Form title
    async updateDisciplineCustomLabel(customTitle) {
      if (this.currentDiscipline) {
        this.currentDiscipline.workFormCustomTitle = customTitle;

        var result = await updateDisciplineAsync(this.currentDiscipline.id, this.currentDiscipline);

        if (result) {
          store.dispatch("setSnackbar", {
            title: "Succès",
            text: "Le titre personnalisé a bien été mis à jour.",
            status: "success",
            timeout: 1000,
          });
        }

        this.getDisciplineForCustomLabel();
      } else {
        store.dispatch("setSnackbar", {
          title: "Erreur",
          text: "Impossible de récupérer la discipline auprès du serveur",
          status: "error",
          timeout: 5000,
        });
      }
    },
  },
};
</script>

<style>
.part-title {
  font-family: Roboto, sans-serif;
  font-size: 16px;
  font-weight: 500;
}
.no-vert-padding {
  padding-top: 0;
  padding-bottom: 0;
}
</style>
